import * as React from 'react';
import { type Editor } from '@tiptap/react';

// --- Hooks ---
import { useTiptapEditor } from '../../hooks/use-tiptap-editor';
import { useUiEditorState } from '../../hooks/use-ui-editor-state';
import { useIsMobile } from '../../hooks/use-mobile';
import { useFloatingToolbarVisibility } from '../../hooks/use-floating-toolbar-visibility';

// --- Icons ---
import { Icon } from '../../../../ui/icon/icon';

// --- UI ---
import { ColorTextPopover } from '../../../../ui/tiptap/tiptap-ui/color-text-popover';
import { LinkPopover } from '../../../../ui/tiptap/tiptap-ui/link-popover';
import type { Mark } from '../../../../ui/tiptap/tiptap-ui/mark-button';
import {
  canToggleMark,
  MarkButton,
} from '../../../../ui/tiptap/tiptap-ui/mark-button';
import type { TextAlign } from '../../../../ui/tiptap/tiptap-ui/text-align-button';
import {
  canSetTextAlign,
  TextAlignButton,
} from '../../../../ui/tiptap/tiptap-ui/text-align-button';
import { TurnIntoDropdown } from '../../../../ui/tiptap/tiptap-ui/turn-into-dropdown';
import { TableControlsButton } from '../../../../ui/tiptap/tiptap-ui/table-controls-button';
import { CommentButton } from '../comments';

// --- Utils ---
import { isSelectionValid } from '../../../../ui/tiptap/utils';

// --- UI Components ---
import { Button } from '@cotera/client/v0/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@cotera/client/v0/components/ui/popover';
import {
  Toolbar,
  ToolbarGroup,
  ToolbarSeparator,
} from '../../../../ui/tiptap/tiptap-ui-primitive/toolbar';

// --- UI Utils ---
import { FloatingElement } from '../../../../ui/tiptap/tiptap-ui-utils/floating-element';

export function FloatingToolbar() {
  const { editor } = useTiptapEditor();
  const isMobile = useIsMobile(480);
  const { lockDragHandle, commentInputVisible } = useUiEditorState(editor);

  const { shouldShow } = useFloatingToolbarVisibility({
    editor,
    isSelectionValid: (editor, selection) => {
      // Don't show toolbar if document is empty or only has whitespace
      const docText = editor.state.doc.textContent.trim();
      if (docText.length === 0) {
        return false;
      }

      return isSelectionValid(editor, selection, [
        'imageUpload',
        'horizontalRule',
        'jsonTypeBuilder',
      ]);
    },
    extraHideWhen: commentInputVisible, // Yield to the comment input popover
  });

  if (lockDragHandle || isMobile) {
    return null;
  }

  return (
    <FloatingElement shouldShow={shouldShow}>
      <Toolbar variant="floating">
        <ToolbarGroup>
          <TurnIntoDropdown hideWhenUnavailable={true} />
        </ToolbarGroup>

        <ToolbarSeparator />

        <ToolbarGroup>
          <MarkButton type="bold" hideWhenUnavailable={true} />
          <MarkButton type="italic" hideWhenUnavailable={true} />
          <MarkButton type="strike" hideWhenUnavailable={true} />
          <MarkButton type="code" hideWhenUnavailable={true} />
        </ToolbarGroup>

        <ToolbarSeparator />

        <ToolbarGroup>
          <LinkPopover
            autoOpenOnLinkActive={false}
            hideWhenUnavailable={true}
          />
          <ColorTextPopover hideWhenUnavailable={true} />
        </ToolbarGroup>

        <ToolbarGroup>
          <TableControlsButton />
          <CommentButton />
        </ToolbarGroup>

        <MoreOptions hideWhenUnavailable={true} />
      </Toolbar>
    </FloatingElement>
  );
}

function canMoreOptions(editor: Editor | null): boolean {
  if (!editor) {
    return false;
  }

  const canTextAlignAny = ['left', 'center', 'right', 'justify'].some((align) =>
    canSetTextAlign(editor, align as TextAlign)
  );

  const canMarkAny = ['superscript', 'subscript'].some((type) =>
    canToggleMark(editor, type as Mark)
  );

  return canMarkAny || canTextAlignAny;
}

function shouldShowMoreOptions(params: {
  editor: Editor | null;
  hideWhenUnavailable: boolean;
}): boolean {
  const { editor, hideWhenUnavailable } = params;

  if (!editor) {
    return false;
  }

  if (hideWhenUnavailable && !editor.isActive('code')) {
    return canMoreOptions(editor);
  }

  return Boolean(editor?.isEditable);
}

export interface MoreOptionsProps
  extends Omit<React.ComponentProps<typeof Button>, 'type'> {
  /**
   * The Tiptap editor instance.
   */
  editor?: Editor | null;
  /**
   * Whether to hide the dropdown when no options are available.
   * @default false
   */
  hideWhenUnavailable?: boolean;
}

export function MoreOptions({
  editor: providedEditor,
  hideWhenUnavailable = false,
  ...props
}: MoreOptionsProps) {
  const { editor } = useTiptapEditor(providedEditor);
  const [show, setShow] = React.useState(false);

  React.useEffect(() => {
    if (!editor) {
      return;
    }

    const handleSelectionUpdate = () => {
      setShow(
        shouldShowMoreOptions({
          editor,
          hideWhenUnavailable,
        })
      );
    };

    handleSelectionUpdate();

    editor.on('selectionUpdate', handleSelectionUpdate);

    return () => {
      editor.off('selectionUpdate', handleSelectionUpdate);
    };
  }, [editor, hideWhenUnavailable]);

  if (!show || !editor || !editor.isEditable) {
    return null;
  }

  return (
    <>
      <ToolbarSeparator />
      <ToolbarGroup>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              role="button"
              tabIndex={-1}
              title="More options"
              {...props}
            >
              <Icon icon="more-vertical" className="tiptap-button-icon" />
            </Button>
          </PopoverTrigger>

          <PopoverContent
            side="top"
            align="end"
            alignOffset={4}
            sideOffset={4}
            asChild
          >
            <Toolbar variant="floating" tabIndex={0}>
              <ToolbarGroup>
                <MarkButton type="superscript" />
                <MarkButton type="subscript" />
              </ToolbarGroup>

              <ToolbarSeparator />

              <ToolbarGroup>
                <TextAlignButton align="left" />
                <TextAlignButton align="center" />
                <TextAlignButton align="right" />
                <TextAlignButton align="justify" />
              </ToolbarGroup>

              <ToolbarSeparator />
            </Toolbar>
          </PopoverContent>
        </Popover>
      </ToolbarGroup>
    </>
  );
}
